import styled from 'styled-components';
import {
  FiltersBtn,
  Bar as ContextBarRow
} from '../../components/V2ContextBar';
import { GroupByBtn, GroupByWrap } from './shared';

export const GVBar = styled(ContextBarRow)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  border-radius: 8px;
  padding: 20px 22px 0;
`;

export const GVPrimaryRow = styled.div`
  display: grid;
  grid-template-columns: minmax(190px, 0.72fr) minmax(210px, 0.8fr) minmax(
      0,
      2fr
    );
  align-items: stretch;
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(180px, 1fr) minmax(200px, 1fr);
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

export const GVControlColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  min-width: 0;
  padding-right: 18px;
  border-right: 1px solid var(--line-soft, #e8e5df);

  > div {
    min-height: 0;
    padding: 0;
    gap: 7px;
  }

  @media (max-width: 680px) {
    padding: 0 0 12px;
    border-right: 0;
    border-bottom: 1px solid var(--line-soft, #e8e5df);
  }
`;

export const GVScopeControls = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  gap: 14px;
  min-width: 0;
  padding-left: 4px;

  > div {
    min-width: 0;
    padding: 0;
    gap: 7px;
  }

  @media (max-width: 1100px) {
    grid-column: 1 / -1;
    padding: 14px 0 0;
    border-top: 1px solid var(--line-soft, #e8e5df);
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

export const GVActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
  padding: 14px 0;
  border-top: 1px solid var(--line-soft, #e8e5df);
`;

export const GVFiltersBtn = styled(FiltersBtn)`
  min-height: 44px;
  padding: 9px 20px;
  background: var(--terracotta);
  color: var(--white);
  box-shadow: 0 2px 5px rgba(200, 75, 31, 0.18);

  &:hover {
    background: var(--terracotta-dark, #a63d18);
    color: var(--white);
  }

  svg {
    color: currentColor;
  }

  > span:last-child {
    background: ${({ theme }) => theme?.white ?? 'var(--white)'};
    color: var(--terracotta);
  }
`;

export const SavedViewsBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1.5px solid var(--line);
  background: var(--white);
  color: var(--ink);
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  svg {
    width: 13px;
    height: 13px;
    color: var(--terracotta);
  }

  &:hover {
    border-color: var(--terracotta);
    color: var(--terracotta);
  }
`;

export const SaveViewTriggerBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  background: var(--ink);
  border: none;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--white);
  cursor: pointer;
  transition: background 0.15s;
  margin-bottom: 10px;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: var(--terracotta);
  }
`;

// Light backdrop + fixed, button-anchored popover — mirrors proto's
// #gvSavedViewsOverlay / #gvSavedViewsPopover (not a centered modal).
export const SavedViewsOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(43, 61, 84, 0.08);
  z-index: 120;
`;

export const SavedViewsPopover = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: 360px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 12px;
  box-shadow:
    0 12px 32px -8px rgba(43, 61, 84, 0.2),
    0 4px 12px -4px rgba(43, 61, 84, 0.1);
  z-index: 130;
  padding: 16px;
`;

export const SavedViewsPopHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

export const SavedViewsPopTitle = styled.div`
  font-family: var(--font-display, var(--font-sans));
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

export const SavedViewsCloseBtn = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--ink-faint);
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0;

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover {
    border-color: var(--line);
    color: var(--ink);
    background: var(--paper);
  }
`;

export const SavedViewsSearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid var(--line);
  padding: 9px 12px;
  font-size: 13px;
  margin-bottom: 10px;
`;

export const SavedViewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const EmptySavedViews = styled.div`
  font-size: 12.5px;
  color: var(--ink-faint);
  padding: 10px 4px;
  text-align: center;
`;

export const SavedViewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--paper);
  }
`;

export const SavedViewInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;

  strong {
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    font-size: 11px;
    color: var(--ink-faint);
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const RemoveViewBtn = styled.button`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--ink-faint);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: all 0.15s;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    background: rgba(200, 75, 31, 0.1);
    color: var(--terracotta);
  }
`;

export const SaveViewInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  font-size: 13px;
`;

const PeriodToggleWrap = styled(GroupByWrap)`
  margin-left: 8px;
`;

const PeriodOption = styled(GroupByBtn)`
  &:disabled {
    cursor: not-allowed;
    opacity: 0.42;
  }
`;

const periodOptions = [
  { value: 'latest', label: 'Latest', active: true },
  { value: '3m', label: '3M', active: false },
  { value: '6m', label: '6M', active: false },
  { value: '12m', label: '12M', active: false }
] as const;

export const PeriodToggle = () => (
  <PeriodToggleWrap
    role="radiogroup"
    aria-label="Comparison period for distribution status"
  >
    {periodOptions.map((option) => (
      <PeriodOption
        key={option.value}
        type="button"
        role="radio"
        aria-checked={option.active}
        $active={option.active}
        disabled
      >
        {option.label}
      </PeriodOption>
    ))}
  </PeriodToggleWrap>
);
